import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Loader2, AlertCircle } from 'lucide-react';

const AUTH_TIMEOUT_MS = 30_000;

export function AuthCallback() {
  const navigate = useNavigate();
  const [error, _setError] = useState<string | null>(null);

  useEffect(() => {
    // Check URL hash for OAuth errors
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const oauthError = params.get('error');
      const errorDescription = params.get('error_description');
      if (oauthError) {
        const message = errorDescription || oauthError;
        navigate(`/login?error=${encodeURIComponent(message)}`, { replace: true });
        return;
      }
    }

    // Also check query params
    const searchParams = new URLSearchParams(window.location.search);
    const queryError = searchParams.get('error');
    if (queryError) {
      navigate(`/login?error=${encodeURIComponent(queryError)}`, { replace: true });
      return;
    }

    // Set timeout to prevent infinite spinner
    const timeout = setTimeout(() => {
      navigate('/login?error=timeout', { replace: true });
    }, AUTH_TIMEOUT_MS);

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        clearTimeout(timeout);
        // If the user was in the middle of an OAuth consent flow, restore that URL.
        const pending = sessionStorage.getItem('pending_oauth_request');
        if (pending) {
          sessionStorage.removeItem('pending_oauth_request');
          window.location.href = pending;
          return;
        }
        navigate('/projects', { replace: true });
      } else if (event === 'SIGNED_OUT') {
        clearTimeout(timeout);
        navigate('/login', { replace: true });
      }
    });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial">
        <div className="flex flex-col items-center gap-5 animate-fade-in">
          <div className="w-14 h-14 rounded-xl bg-red-500/20 flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-red-400" />
          </div>
          <p className="text-sm font-semibold text-text-primary">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="text-sm text-accent-violet hover:underline"
          >
            Torna al login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial">
      <div className="flex flex-col items-center gap-5 animate-fade-in">
        <div className="relative">
          <div className="w-14 h-14 rounded-xl bg-linear-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-lg shadow-accent-violet/20">
            <span className="text-lg font-bold text-white">Z</span>
          </div>
          <div className="absolute -right-1 -bottom-1 w-6 h-6 rounded-full bg-surface-primary flex items-center justify-center">
            <Loader2 className="w-4 h-4 text-accent-violet animate-spin" />
          </div>
        </div>
        <p className="text-sm font-semibold text-text-primary">Accesso in corso...</p>
      </div>
    </div>
  );
}
